import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { _ } from "i18n";
import { toNullableInt } from "libs/utils";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import { DISCOVER_GAMES_PRIORITY_FILTER_OPTIONS } from "../../../datagrid-configs/discoverGames";
import { countBulkApplyPreview } from "./discoverGamesBulkApply";
import "./DiscoverGamesBulkUpdateModal.css";

export type DiscoverGamesBulkUpdatePayload = {
	applyClubManager: boolean;
	clubManagerId: number | null;
	applyContentManager: boolean;
	contentManagerId: number | null;
	applyAssignee: boolean;
	assigneeId: number | null;
	applyPriority: boolean;
	priority: number | null;
	applyGameStatus: boolean;
	gameStatus: string | null;
};

type UserOption = { value: string | number; label: string };

/** Per-field eligibility sets used to drive hints, skip confirmation and disabled state. */
export type BulkFieldEligibility = {
	eligible: ReadonlySet<number>;
	ineligible: ReadonlySet<number>;
};

export type BulkEligibility = {
	/** Total distinct ids evaluated by the backend. */
	total: number;
	isLoading: boolean;
	/**
	 * True while the backend response is still missing (e.g. first render of
	 * the modal). When true the modal assumes every selected id is eligible
	 * until the real response comes in.
	 */
	hasData: boolean;
	clubManager: BulkFieldEligibility;
	contentManager: BulkFieldEligibility;
	assignee: BulkFieldEligibility;
	priority: BulkFieldEligibility;
	gameStatus: BulkFieldEligibility;
};

export enum BulkField {
	ClubManager = "clubManager",
	ContentManager = "contentManager",
	Assignee = "assignee",
	Priority = "priority",
	GameStatus = "gameStatus",
}
export type BulkFieldKey = BulkField;

/** Applied to every selected row when checked; no per-row eligibility hint or disable. */
const BULK_FIELDS_WITHOUT_ELIGIBILITY_RESTRICTION: readonly BulkFieldKey[] = [
	BulkField.ClubManager,
	BulkField.ContentManager,
];
const bulkFieldsWithoutEligibilityRestriction = new Set<BulkFieldKey>(
	BULK_FIELDS_WITHOUT_ELIGIBILITY_RESTRICTION,
);

interface DiscoverGamesBulkUpdateModalProps {
	isOpen: boolean;
	onClose: () => void;
	selectedIds: ReadonlySet<number>;
	selectedCount: number;
	clubManagerOptions: UserOption[];
	contentManagerOptions: UserOption[];
	assigneeOptions: UserOption[];
	gameStatusOptions: UserOption[];
	isSubmitting: boolean;
	/**
	 * Per-field eligibility for the currently selected ids. The modal reads
	 * only `.size` of the Sets; passing a stable reference is fine.
	 */
	eligibility: BulkEligibility;
	onApply: (payload: DiscoverGamesBulkUpdatePayload) => void | Promise<void>;
	canEditGameUploads?: boolean;
	canEditAssignee?: boolean;
	canEditGameStatus?: boolean;
	canEditPriority?: boolean;
}

const emptyPayload = (): DiscoverGamesBulkUpdatePayload => ({
	applyClubManager: false,
	clubManagerId: null,
	applyContentManager: false,
	contentManagerId: null,
	applyAssignee: false,
	assigneeId: null,
	applyPriority: false,
	priority: null,
	applyGameStatus: false,
	gameStatus: null,
});

export function DiscoverGamesBulkUpdateModal({
	isOpen,
	onClose,
	selectedIds,
	selectedCount,
	clubManagerOptions,
	contentManagerOptions,
	assigneeOptions,
	gameStatusOptions,
	isSubmitting,
	eligibility,
	onApply,
	canEditGameUploads = true,
	canEditAssignee = true,
	canEditGameStatus = true,
	canEditPriority = true,
}: DiscoverGamesBulkUpdateModalProps) {
	const [payload, setPayload] = useState<DiscoverGamesBulkUpdatePayload>(emptyPayload);
	const [confirmSkip, setConfirmSkip] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setPayload(emptyPayload());
			setConfirmSkip(false);
		}
	}, [isOpen]);

	const prioritySelectItems = useMemo(
		() =>
			DISCOVER_GAMES_PRIORITY_FILTER_OPTIONS.map((item) => ({
				value: String(item.value),
				label: item.label,
			})),
		[],
	);

	const mapUserOptions = useCallback((opts: UserOption[]) => {
		return opts.map((item) => ({
			value: String(item.value),
			label: item.label,
		}));
	}, []);

	const clubMgrItems = useMemo(() => mapUserOptions(clubManagerOptions), [clubManagerOptions, mapUserOptions]);
	const contentMgrItems = useMemo(() => mapUserOptions(contentManagerOptions), [contentManagerOptions, mapUserOptions]);
	const assigneeItems = useMemo(() => mapUserOptions(assigneeOptions), [assigneeOptions, mapUserOptions]);
	const statusItems = useMemo(() => mapUserOptions(gameStatusOptions), [gameStatusOptions, mapUserOptions]);

	const hasAnyApply = useMemo(
		() =>
			payload.applyClubManager ||
			payload.applyContentManager ||
			payload.applyAssignee ||
			payload.applyPriority ||
			payload.applyGameStatus,
		[
			payload.applyClubManager,
			payload.applyContentManager,
			payload.applyAssignee,
			payload.applyPriority,
			payload.applyGameStatus,
		],
	);

	/** Counts for each field, used for hints and the skip-confirm banner. */
	const fieldCounts = useMemo(() => {
		const total = selectedCount;
		const counts: Record<BulkFieldKey, { eligible: number; ineligible: number; total: number; applied: boolean }> = {
			clubManager: {
				eligible: eligibility.clubManager.eligible.size,
				ineligible: eligibility.clubManager.ineligible.size,
				total,
				applied: payload.applyClubManager,
			},
			contentManager: {
				eligible: eligibility.contentManager.eligible.size,
				ineligible: eligibility.contentManager.ineligible.size,
				total,
				applied: payload.applyContentManager,
			},
			assignee: {
				eligible: eligibility.assignee.eligible.size,
				ineligible: eligibility.assignee.ineligible.size,
				total,
				applied: payload.applyAssignee,
			},
			priority: {
				eligible: eligibility.priority.eligible.size,
				ineligible: eligibility.priority.ineligible.size,
				total,
				applied: payload.applyPriority,
			},
			gameStatus: {
				eligible: eligibility.gameStatus.eligible.size,
				ineligible: eligibility.gameStatus.ineligible.size,
				total,
				applied: payload.applyGameStatus,
			},
		};
		return counts;
	}, [eligibility, selectedCount, payload]);

	const { applyCount, skippedCount } = useMemo(
		() => countBulkApplyPreview(selectedIds, selectedCount, payload, eligibility),
		[selectedIds, selectedCount, payload, eligibility],
	);

	const needsSkipConfirmation = useMemo(
		() => eligibility.hasData && hasAnyApply && skippedCount > 0,
		[eligibility.hasData, hasAnyApply, skippedCount],
	);

	/** Fields gated by backend eligibility; club/content manager apply to all selected rows. */
	const needsEligibilityData = useMemo(
		() => payload.applyAssignee || payload.applyPriority || payload.applyGameStatus,
		[payload.applyAssignee, payload.applyPriority, payload.applyGameStatus],
	);

	// Require a fresh acknowledgment when eligibility loads or apply/skip counts change.
	useEffect(() => {
		setConfirmSkip(false);
	}, [needsSkipConfirmation, skippedCount, applyCount]);

	const clubManagerValue = useMemo(
		() =>
			clubMgrItems.find(
				(item) => item.value === (payload.clubManagerId === null ? "" : String(payload.clubManagerId)),
			) ?? null,
		[clubMgrItems, payload.clubManagerId],
	);

	const contentManagerValue = useMemo(
		() =>
			contentMgrItems.find(
				(item) => item.value === (payload.contentManagerId === null ? "" : String(payload.contentManagerId)),
			) ?? null,
		[contentMgrItems, payload.contentManagerId],
	);

	const assigneeValue = useMemo(
		() =>
			assigneeItems.find(
				(item) => item.value === (payload.assigneeId === null ? "" : String(payload.assigneeId)),
			) ?? null,
		[assigneeItems, payload.assigneeId],
	);

	const priorityValue = useMemo(
		() =>
			prioritySelectItems.find(
				(item) => item.value === (payload.priority === null ? "" : String(payload.priority)),
			) ?? null,
		[prioritySelectItems, payload.priority],
	);

	const gameStatusValue = useMemo(
		() =>
			statusItems.find(
				(item) => item.value === (payload.gameStatus === null ? "" : String(payload.gameStatus)),
			) ?? null,
		[statusItems, payload.gameStatus],
	);

	const canSubmit = useMemo(
		() =>
			hasAnyApply &&
			selectedCount > 0 &&
			isSubmitting === false &&
			applyCount > 0 &&
			(needsEligibilityData === false || eligibility.hasData) &&
			(needsSkipConfirmation === false || confirmSkip) &&
			(payload.applyPriority === false || payload.priority !== null) &&
			(payload.applyGameStatus === false ||
				(payload.gameStatus !== null && String(payload.gameStatus).trim() !== "")) &&
			(payload.applyClubManager === false || payload.clubManagerId !== null) &&
			(payload.applyContentManager === false || payload.contentManagerId !== null) &&
			(payload.applyAssignee === false || payload.assigneeId !== null),
		[
			hasAnyApply,
			selectedCount,
			isSubmitting,
			applyCount,
			needsEligibilityData,
			eligibility.hasData,
			needsSkipConfirmation,
			confirmSkip,
			payload.applyPriority,
			payload.priority,
			payload.applyGameStatus,
			payload.gameStatus,
			payload.applyClubManager,
			payload.clubManagerId,
			payload.applyContentManager,
			payload.contentManagerId,
			payload.applyAssignee,
			payload.assigneeId,
		],
	);

	const renderFieldHint = useCallback(
		(key: BulkFieldKey) => {
			if (bulkFieldsWithoutEligibilityRestriction.has(key)) {
				return null;
			}
			const info = fieldCounts[key];
			if (!eligibility.hasData) {
				if (eligibility.isLoading) {
					return (
						<span className="discoverGamesBulkModal-fieldHint discoverGamesBulkModal-fieldHint--muted">
							{_("discover.gamesTable.bulkUpdate.checkingEligibility")}
						</span>
					);
				}
				return null;
			}
			if (info.eligible === 0) {
				return (
					<span className="discoverGamesBulkModal-fieldHint discoverGamesBulkModal-fieldHint--warn">
						{_("discover.gamesTable.bulkUpdate.noneEligible")}
					</span>
				);
			}
			const applied = info.eligible;
			const hintClass = info.applied && info.ineligible > 0
				? "discoverGamesBulkModal-fieldHint discoverGamesBulkModal-fieldHint--warn"
				: "discoverGamesBulkModal-fieldHint";
			return (
				<span className={hintClass}>
					{_("discover.gamesTable.bulkUpdate.fieldCountHint", {
						count: String(applied),
						total: String(info.total),
					})}
				</span>
			);
		},
		[eligibility.hasData, eligibility.isLoading, fieldCounts],
	);

	const isFieldDisabled = useCallback((key: BulkFieldKey): boolean => {
		if (bulkFieldsWithoutEligibilityRestriction.has(key) === true) {
			if (key === BulkField.ClubManager && canEditGameUploads === false) {
				return true;
			}
			if (key === BulkField.ContentManager && canEditGameUploads === false) {
				return true;
			}
			return false;
		}
		if (key === BulkField.Assignee && canEditAssignee === false) {
			return true;
		}
		if (key === BulkField.GameStatus && canEditGameStatus === false) {
			return true;
		}
		if (key === BulkField.Priority && canEditPriority === false) {
			return true;
		}
		return eligibility.hasData && fieldCounts[key].eligible === 0;
	}, [canEditGameUploads, canEditAssignee, canEditGameStatus, canEditPriority, eligibility.hasData, fieldCounts]);

	const handleSubmit = useCallback(
		async (e: FormEvent) => {
			e.preventDefault();
			if (!canSubmit) {
				return;
			}
			await onApply(payload);
		},
		[canSubmit, onApply, payload],
	);

	if (!isOpen) {
		return null;
	}

	return createPortal(
		<div className="discoverGamesBulkModal-overlay" role="presentation" onClick={onClose}>
			<div
				className="discoverGamesBulkModal"
				role="dialog"
				aria-modal="true"
				aria-labelledby="discoverGamesBulkModal-title"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="discoverGamesBulkModal-header">
					<h2 id="discoverGamesBulkModal-title" className="discoverGamesBulkModal-title">
						{_("discover.gamesTable.bulkUpdate.title")}
					</h2>
					<button
						type="button"
						className="discoverGamesBulkModal-closeBtn"
						onClick={onClose}
						aria-label={_("discover.gamesTable.bulkUpdate.closeAria")}
					>
						×
					</button>
				</div>

				<form className="discoverGamesBulkModal-body" onSubmit={handleSubmit}>
					<p className="discoverGamesBulkModal-summary">
						{_("discover.gamesTable.bulkUpdate.selectedCount", { count: String(selectedCount) })}
					</p>
					<p className="discoverGamesBulkModal-hint">{_("discover.gamesTable.bulkUpdate.hint")}</p>

					{canEditGameUploads && (
						<div className="discoverGamesBulkModal-field">
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={payload.applyClubManager}
									disabled={isFieldDisabled(BulkField.ClubManager) || isSubmitting}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											applyClubManager: e.target.checked,
										}))
									}
								/>
								<span>{_("discover.gamesTable.column.clubManager")}</span>
								{renderFieldHint(BulkField.ClubManager)}
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
								value={clubManagerValue}
								items={clubMgrItems}
								isCleareable={false}
								isDisabled={!payload.applyClubManager || isSubmitting || isFieldDisabled(BulkField.ClubManager)}
								handleChange={(option) => {
									setPayload((p) => ({
										...p,
										clubManagerId: toNullableInt(option?.value ?? null),
									}));
								}}
							/>
						</div>
					)}

					{canEditGameUploads && (
						<div className="discoverGamesBulkModal-field">
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={payload.applyContentManager}
									disabled={isFieldDisabled(BulkField.ContentManager) || isSubmitting}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											applyContentManager: e.target.checked,
										}))
									}
								/>
								<span>{_("discover.gamesTable.column.contentManager")}</span>
								{renderFieldHint(BulkField.ContentManager)}
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
								value={contentManagerValue}
								items={contentMgrItems}
								isCleareable={false}
								isDisabled={!payload.applyContentManager || isSubmitting || isFieldDisabled(BulkField.ContentManager)}
								handleChange={(option) => {
									setPayload((p) => ({
										...p,
										contentManagerId: toNullableInt(option?.value ?? null),
									}));
								}}
							/>
						</div>
					)}

					{canEditAssignee && (
						<div className="discoverGamesBulkModal-field">
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={payload.applyAssignee}
									disabled={isFieldDisabled(BulkField.Assignee) || isSubmitting}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											applyAssignee: e.target.checked,
										}))
									}
								/>
								<span>{_("discover.gamesTable.column.assignee")}</span>
								{renderFieldHint(BulkField.Assignee)}
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.programGame.placeholder.unfilled")}
								value={assigneeValue}
								items={assigneeItems}
								isCleareable={false}
								isDisabled={!payload.applyAssignee || isSubmitting || isFieldDisabled(BulkField.Assignee)}
								handleChange={(option) => {
									setPayload((p) => ({
										...p,
										assigneeId: toNullableInt(option?.value ?? null),
									}));
								}}
							/>
						</div>
					)}

					{canEditPriority && (
						<div className="discoverGamesBulkModal-field">
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={payload.applyPriority}
									disabled={isFieldDisabled(BulkField.Priority) || isSubmitting}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											applyPriority: e.target.checked,
										}))
									}
								/>
								<span>{_("discover.gamesTable.column.priority")}</span>
								{renderFieldHint(BulkField.Priority)}
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.cellValue.none")}
								value={priorityValue}
								items={prioritySelectItems}
								isDisabled={!payload.applyPriority || isSubmitting || isFieldDisabled(BulkField.Priority)}
								handleChange={(option) => {
									setPayload((p) => ({
										...p,
										priority: toNullableInt(option?.value ?? null),
									}));
								}}
							/>
						</div>
					)}

					{canEditGameStatus && (
						<div className="discoverGamesBulkModal-field">
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={payload.applyGameStatus}
									disabled={isFieldDisabled(BulkField.GameStatus) || isSubmitting}
									onChange={(e) =>
										setPayload((p) => ({
											...p,
											applyGameStatus: e.target.checked,
										}))
									}
								/>
								<span>{_("discover.gamesTable.column.gameStatus")}</span>
								{renderFieldHint(BulkField.GameStatus)}
							</label>
							<SelectComponent
								placeholder={_("discover.gamesTable.cellValue.none")}
								value={gameStatusValue}
								items={statusItems}
								isCleareable
								isDisabled={!payload.applyGameStatus || isSubmitting || isFieldDisabled(BulkField.GameStatus)}
								handleChange={(option) => {
									const v = option?.value;
									setPayload((p) => ({
										...p,
										gameStatus:
											v === undefined || v === null || v === ""
												? null
												: typeof v === "string"
													? v
													: String(v),
									}));
								}}
							/>
						</div>
					)}

					{needsSkipConfirmation && (
						<div className="discoverGamesBulkModal-skipNotice">
							<p className="discoverGamesBulkModal-skipNotice-summary">
								{_("discover.gamesTable.bulkUpdate.skipSummary", {
									skip: String(skippedCount),
									apply: String(applyCount),
								})}
							</p>
							<label className="discoverGamesBulkModal-checkRow">
								<input
									type="checkbox"
									checked={confirmSkip}
									onChange={(e) => setConfirmSkip(e.target.checked)}
								/>
								<span>
									{_("discover.gamesTable.bulkUpdate.skipConfirm", {
										skip: String(skippedCount),
										apply: String(applyCount),
									})}
								</span>
							</label>
						</div>
					)}

					<div className="discoverGamesBulkModal-footer">
						<button type="button" className="discoverGamesBulkModal-btnSecondary" onClick={onClose} disabled={isSubmitting}>
							{_("discover.gamesTable.bulkUpdate.cancel")}
						</button>
						<button
							type="submit"
							className="discoverGamesBulkModal-btnPrimary"
							disabled={!canSubmit}
						>
							{isSubmitting
								? _("discover.gamesTable.bulkUpdate.applying")
								: _("discover.gamesTable.bulkUpdate.apply")}
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body,
	);
}
